import { css } from '@emotion/css';
import game3rdApi from 'apis/game3rdApi';
import Spinner from 'components/loading/spinner';
import Modal from 'components/modal/Modal';
import { colors } from 'constants/theme';
import { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { IoMdClose } from 'react-icons/io';

interface GameItem {
    gameCode: string;
    gameName: string;
    gameType: string;
    imageUrl: string;
    productCode: number;
    productId: number;
    status: string;
    supportCurrency: string;
}

interface Props {
    dataGame?: { title: string; product: string; category: string };
    onClose?: () => void;
}

const GameList = (props: Props) => {
    const { dataGame, onClose } = props;

    const [gameList, setGameList] = useState<GameItem[]>([]);

    useEffect(() => {
        const GetGamesList = async () => {
            if (dataGame) {
                setGameList([]);
                Spinner.show();
                try {
                    const payloadGameList = {
                        product: dataGame.product,
                        category: dataGame.category,
                    };
                    const { data } = await game3rdApi.gameList3RD(payloadGameList);

                    if (data.msg) {
                        toast.error(data.msg);
                    } else {
                        setGameList(data.gameList);
                    }
                } catch (error) {
                    console.log(error);
                    toast.error('Không thể tải danh sách trò chơi');
                } finally {
                    Spinner.hide();
                }
            } else {
                setGameList([]);
            }
        };
        GetGamesList();
    }, [dataGame]);

    const onClickGame = async (game: GameItem) => {
        Spinner.show();
        try {
            const { data } = await game3rdApi.launchGame({ product: game.productCode, category: game.gameType, gameid: game.gameCode, html5: 'WEB' } as any);

            if (data.msg) {
                toast.error(data.msg);
            } else if (data?.url) {
                if (dataGame?.title === 'PGSOFT' && dataGame.category === 'SLOT') {
                    const newWindow = window.open('', '_blank');
                    if (newWindow) {
                        newWindow.document.write(data.url);
                        newWindow.document.close();
                    }
                } else {
                    window.open(data.url, '_blank');
                }
            }
        } catch (error) {
            console.log(error);
            toast.error('Không thể mở trò chơi');
        } finally {
            Spinner.hide();
        }
    };

    if (!dataGame) return null;

    return (
        <Modal isShow={true} onClose={() => onClose?.()} width="1120px" maxWidth="calc(100vw - 40px)">
            <div className={styles.container}>
                <div className={styles.heading}>
                    <div>
                        <p className={styles.eyebrow}>THƯ VIỆN TRÒ CHƠI</p>
                        <h6 className={styles.title}>{dataGame.title.toUpperCase()}</h6>
                    </div>
                    <button type="button" className={styles.btnClose} onClick={onClose} aria-label="Đóng danh sách trò chơi">
                        <IoMdClose size={24} />
                    </button>
                </div>

                <div className={styles.listGame}>
                    {gameList.length ? (
                        gameList.map((item, index) => (
                            <button type="button" className={styles.itemGame} onClick={() => onClickGame?.(item)} key={index} style={{ animationDelay: `${Math.min(index, 9) * 35}ms` }}>
                                <img alt={item.gameName} src={item.imageUrl} className={styles.image} />
                                <span className="game-name">{item.gameName}</span>
                                <span className="play-game">CHƠI NGAY</span>
                            </button>
                        ))
                    ) : (
                        <div className="no-data">Không có dữ liệu</div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default memo(GameList);

const styles = {
    container: css`
        display: flex;
        flex-direction: column;
        gap: 18px;
        height: min(760px, calc(100vh - 80px));
        padding: 24px;
        overflow: hidden;
        border: 1px solid rgba(255, 217, 138, .55);
        border-radius: 14px;
        background: radial-gradient(circle at top right, rgba(51, 91, 160, .34), transparent 35%), linear-gradient(145deg, #16294a, #0a1830 70%);
        box-shadow: 0 24px 70px rgba(0, 0, 0, .46);
        @media (max-width: 640px) { height: calc(100vh - 32px); padding: 16px; border-radius: 10px; }
    `,
    itemGame: css`
        min-width: 0;
        display: flex;
        position: relative;
        flex-direction: column;
        aspect-ratio: .84;
        overflow: hidden;
        padding: 0;
        border: 1px solid rgba(124, 148, 234, .4);
        border-radius: 9px;
        background: #0d1d39;
        cursor: pointer;
        animation: gameCardEnter 300ms ease both;
        transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
        &::after { content: ''; position: absolute; inset: 36% 0 0; background: linear-gradient(transparent, rgba(3, 12, 28, .94)); pointer-events: none; }
        &:hover {
            transform: translateY(-5px);
            border-color: ${colors.secondary};
            box-shadow: 0 14px 24px rgba(0, 0, 0, .38), 0 0 16px rgba(255, 173, 51, .16);
            .image { transform: scale(1.06); filter: saturate(1.1) brightness(1.08); }
            .play-game { opacity: 1; transform: translateY(0); }
        }
        &:active { transform: scale(.98); }
        .game-name { position: absolute; z-index: 1; right: 10px; bottom: 10px; left: 10px; overflow: hidden; color: ${colors.white}; font-size: 12px; font-weight: 800; line-height: 1.3; text-align: left; text-overflow: ellipsis; text-shadow: 0 2px 5px #000; white-space: nowrap; }
        .play-game { position: absolute; z-index: 2; top: 10px; right: 10px; padding: 5px 7px; border: 1px solid rgba(255, 217, 138, .8); border-radius: 99px; background: rgba(5, 18, 38, .72); color: #fff1b6; font-size: 8px; font-weight: 800; opacity: 0; transform: translateY(-5px); transition: opacity 160ms ease, transform 160ms ease; }

        @keyframes gameCardEnter {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
    `,
    listGame: css`
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 14px;
        padding: 2px 4px 8px;
        overflow-y: auto;
        scrollbar-color: rgba(255, 217, 138, .7) transparent;
        scrollbar-width: thin;
        @media (max-width: 880px) { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        @media (max-width: 660px) { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
        @media (max-width: 440px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }

        .no-data {
            grid-column: 1 / -1;
            width: 100%;
            margin-top: 80px;
            text-align: center;
            color: #cad7ed;
        }
    `,
    image: css`
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 220ms ease, filter 220ms ease;
    `,
    eyebrow: css`margin-bottom: 4px; color: #f4d681; font-size: 10px; font-weight: 800; letter-spacing: .14em;`,
    title: css`
        color: ${colors.white};
        font-size: 24px;
        line-height: 1.1;
        text-transform: uppercase;
    `,
    btnClose: css`
        display: grid;
        width: 38px;
        height: 38px;
        place-items: center;
        border: 1px solid rgba(255, 217, 138, .45);
        border-radius: 50%;
        color: #fff1b6;
        background: rgba(255, 255, 255, .06);
        cursor: pointer;
        transition: transform 160ms ease, background 160ms ease;
        &:hover { transform: rotate(90deg); background: rgba(255, 173, 51, .2); }
    `,
    heading: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-bottom: 16px;
        border-bottom: 1px solid rgba(124, 148, 234, .28);
    `,
};
